"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { ReactNode } from "react";
import { useSidebarLayout } from "@/layouts/app-shell/sidebar-layout";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import type { NavigationIconComponent } from "@/types/sidebar";
import classes from "./navigation-item.module.css";
type NavigationIconState = "default" | "active";
interface NavigationIconProps {
  icon: NavigationIconComponent;
  state?: NavigationIconState;
  className?: string;
}
export function NavigationIcon(t0) {
  const $ = _c(8);
  const {
    icon: Icon,
    state: t1,
    className
  } = t0;
  const state = t1 === undefined ? "default" : t1;
  const t2 = state === "active" ? classes.iconColorActive : classes.iconColorDefault;
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn(classes.icon, t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== Icon) {
    t4 = <Icon className={classes.iconSvg} />;
    $[3] = Icon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <span className={t3} aria-hidden="true">{t4}</span>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function NavigationItemLabel(t0) {
  const $ = _c(6);
  const {
    children,
    state: t1,
    className
  } = t0;
  const state = t1 === undefined ? "default" : t1;
  const t2 = state === "active" ? classes.itemLabelColorActive : classes.itemLabelColorDefault;
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn(classes.itemLabel, t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <span className={t3}>{children}</span>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
interface NavigationItemProps {
  icon: NavigationIconComponent;
  label: string;
  href: string;
  isActive?: boolean;
  isCollapsed?: boolean;
  onClick?: () => void;
  className?: string;
}
export function NavigationItem(t0) {
  const $ = _c(35);
  const {
    icon,
    label,
    href,
    isActive: t1,
    isCollapsed: t2,
    onClick,
    className
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const isCollapsed = t2 === undefined ? false : t2;
  const {
    phase,
    isMobile,
    setOpen
  } = useSidebarLayout();
  const isDesktop = useMediaQuery("(min-width: 1025px)");
  const isClosing = phase === "menu-out" && !isMobile;
  const isOpening = phase === "menu-in" && !isMobile;
  const showExpandedRow = !isCollapsed || phase === "menu-out";
  let t3;
  if ($[0] !== isCollapsed || $[1] !== isDesktop || $[2] !== setOpen) {
    t3 = () => {
      if (!isDesktop || !isCollapsed) {
        return;
      }
      setOpen(true);
    };
    $[0] = isCollapsed;
    $[1] = isDesktop;
    $[2] = setOpen;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isCollapsed ? label : undefined;
  const t5 = showExpandedRow && classes.rootWithExpandedRow;
  const t6 = isClosing && classes.rootClosing;
  const t7 = isCollapsed && classes.rootCollapsed;
  let t8;
  if ($[4] !== className || $[5] !== t5 || $[6] !== t6 || $[7] !== t7) {
    t8 = cn(classes.root, t5, t6, t7, className);
    $[4] = className;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== isActive || $[10] !== isClosing || $[11] !== isOpening || $[12] !== label || $[13] !== showExpandedRow) {
    t9 = showExpandedRow && <span className={cn(classes.expandedLayer, isActive && classes.expandedLayerActive)}><span className={cn(classes.activeIndicator, classes.indicator, isClosing && isActive && classes.indicatorClosing)} aria-hidden="true" /><span className={classes.iconSpacer} aria-hidden="true" /><NavigationItemLabel state={isActive ? "active" : "default"} className={cn(isOpening && classes.labelOpening, isClosing && classes.labelClosing)}>{label}</NavigationItemLabel></span>;
    $[9] = isActive;
    $[10] = isClosing;
    $[11] = isOpening;
    $[12] = label;
    $[13] = showExpandedRow;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = isCollapsed && classes.iconStageCollapsed;
  let t11;
  if ($[15] !== t10) {
    t11 = cn(classes.iconStage, t10);
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== isClosing || $[18] !== isCollapsed) {
    t12 = (isCollapsed || isClosing) && <span className={classes.backdrop} aria-hidden="true" />;
    $[17] = isClosing;
    $[18] = isCollapsed;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = isActive ? "active" : "default";
  let t14;
  if ($[20] !== icon || $[21] !== t13) {
    t14 = <NavigationIcon icon={icon} state={t13} />;
    $[20] = icon;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t14) {
    t15 = <span className={t11}>{t12}{t14}</span>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== href || $[28] !== onClick || $[29] !== t15 || $[30] !== t3 || $[31] !== t4 || $[32] !== t8 || $[33] !== t9) {
    t16 = <Link href={href} onClick={onClick} onMouseEnter={t3} title={t4} className={t8}>{t9}{t15}</Link>;
    $[27] = href;
    $[28] = onClick;
    $[29] = t15;
    $[30] = t3;
    $[31] = t4;
    $[32] = t8;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}